/**
 * Admin Portal Authentication Context
 * 
 * Simplified auth context for platform administrators
 */

import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { AuthState, AuthContextType, LoginRequest, AuthResponse, User, ROLES } from '../types/auth';

// Initial state
const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  tokens: null,
  permissions: [],
  error: null,
};

// Auth reducer
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; tokens: any; permissions: string[] } }
  | { type: 'AUTH_ERROR'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'CLEAR_ERROR' };

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_START':
      return { ...state, isLoading: true, error: null };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: true,
        user: action.payload.user,
        tokens: action.payload.tokens,
        permissions: action.payload.permissions,
        error: null,
      };
    case 'AUTH_ERROR':
      return {
        ...state,
        isLoading: false,
        isAuthenticated: false,
        user: null,
        tokens: null,
        permissions: [],
        error: action.payload,
      };
    case 'AUTH_LOGOUT':
      return {
        ...initialState,
        isLoading: false,
      };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth provider component
interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Check for existing session on mount
  useEffect(() => {
    checkExistingSession();
  }, []);

  const checkExistingSession = async () => {
    try {
      const token = localStorage.getItem('admin_access_token');
      if (!token) {
        dispatch({ type: 'AUTH_LOGOUT' });
        return;
      }

      // Verify token with the API
      try {
        const response = await fetch('/api/auth/verify', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          
          // Verify the user has platform admin role
          if (data.data.user.role === ROLES.PLATFORM_ADMIN) {
            dispatch({
              type: 'AUTH_SUCCESS',
              payload: {
                user: data.data.user,
                tokens: { 
                  accessToken: token, 
                  refreshToken: localStorage.getItem('admin_refresh_token') || '' 
                },
                permissions: data.data.user.permissions,
              },
            });
            return;
          }
        }
      } catch (error) {
        console.log('Token verification failed:', error);
      }

      // If we get here, the token is invalid or user is not platform admin
      localStorage.removeItem('admin_access_token');
      localStorage.removeItem('admin_refresh_token');
      dispatch({ type: 'AUTH_LOGOUT' });
    } catch (error) {
      console.error('Session check failed:', error);
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  };

  const login = async (credentials: LoginRequest): Promise<AuthResponse> => {
    dispatch({ type: 'AUTH_START' });

    try {
      // Use real API authentication instead of mock
      const response = await fetch('/api/b2b/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: credentials.email,
          password: credentials.password
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Login failed');
      }

      const data = await response.json();
      
      // Verify the user has platform admin role
      if (data.data.user.role !== ROLES.PLATFORM_ADMIN) {
        throw new Error('Access denied. Platform administrator privileges required.');
      }

      // Store real tokens
      localStorage.setItem('admin_access_token', data.data.accessToken);
      localStorage.setItem('admin_refresh_token', data.data.refreshToken);

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: data.data.user,
          tokens: {
            accessToken: data.data.accessToken,
            refreshToken: data.data.refreshToken
          },
          permissions: data.data.permissions,
        },
      });

      return {
        accessToken: data.data.accessToken,
        refreshToken: data.data.refreshToken,
        user: data.data.user,
        permissions: data.data.permissions
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      dispatch({ type: 'AUTH_ERROR', payload: errorMessage });
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      // Call real logout API
      const refreshToken = localStorage.getItem('admin_refresh_token');
      if (refreshToken) {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ refreshToken })
        });
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      // Clear local storage
      localStorage.removeItem('admin_access_token');
      localStorage.removeItem('admin_refresh_token');
      
      dispatch({ type: 'AUTH_LOGOUT' });
    }
  };

  const hasPermission = (permission: string): boolean => {
    if (!state.isAuthenticated || !state.user) return false;
    
    // Platform admins have all permissions
    if (state.user.role === ROLES.PLATFORM_ADMIN) return true;
    
    return state.permissions.includes(permission) || state.permissions.includes('platform:*');
  };

  const hasRole = (role: string): boolean => {
    return state.user?.role === role;
  };

  const isPlatformAdmin = (): boolean => {
    return state.user?.role === ROLES.PLATFORM_ADMIN;
  };

  const contextValue: AuthContextType = {
    authState: state,
    login,
    logout,
    hasPermission,
    hasRole,
    isPlatformAdmin,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook to use auth context
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}